import type {ReactElement} from 'react';
import React from 'react';
import {BaseItemMultiplyOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-multiply-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromEffectsOfActiveSlideItem} from '@Hooks/poster-editor/useGetKeyFromEffectsOfActiveSlideItem';
import type {RGBA} from '@Utils/color.util';

interface SlideshowItemMultiplyOptionProps {
  onClickColorFill(): void;
}

export function SlideshowItemMultiplyOption({...props}: SlideshowItemMultiplyOptionProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const multiplyColor = useGetKeyFromEffectsOfActiveSlideItem('multiplyColor');
  const itemMultiply = useGetKeyFromEffectsOfActiveSlideItem('multiply');

  if (!activeItem || !activeSlideUID) {
    throw new Error('Active item or active slide is not defined in slideshow item effects options');
  }

  if (!activeItem.isSlideshow()) {
    throw new Error(`Expected active item to be of type slideshow, got ${activeItem.gitype} instead`);
  }

  if (itemMultiply === undefined || !multiplyColor) {
    throw new Error('blackandwhite is undefined for active slide item');
  }

  const onToggleMultiply = (): void => {
    void activeItem.updateSlideFromObject(activeSlideUID, {
      effects: {
        multiply: !itemMultiply,
      },
    });
  };

  const updateOpacity = (multiplyOpacity: number, undoable = true): void => {
    const activeSlideItemMultiplyColor = multiplyColor.slice() as RGBA;
    activeSlideItemMultiplyColor[3] = multiplyOpacity;
    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        effects: {
          multiplyColor: activeSlideItemMultiplyColor,
        },
      },
      {
        undoable,
      }
    );
  };

  return (
    <BaseItemMultiplyOption
      multiplyColor={multiplyColor}
      multiply={itemMultiply}
      multiplyOpacity={multiplyColor[3]}
      onClickColorFill={(): void => {
        props.onClickColorFill();
      }}
      onToggleMultiply={onToggleMultiply}
      updateOpacity={updateOpacity}
    />
  );
}
